import { provideAppInitializer } from '@angular/core';
import type { BrowserOptions, ErrorEvent as SentryErrorEvent } from '@sentry/browser';

/**
 * Frontend Sentry error reporting (#2191).
 *
 * MIRRORS the backend posture in `apps/back/api/src/app/sentry.config.ts`
 * (D6) — a SEPARATE browser DSN/project (never the backend's `SENTRY_DSN`),
 * errors only (no tracing, session replay, or performance capture), and the
 * same scrub-before-send discipline. The PII/secret regexes are
 * re-implemented locally rather than imported: ui-core is a browser bundle
 * and cannot depend on the backend-only `@bodyanalytics/common-api`
 * `redactPii`/`redactPiiMeta` utils. Keep the two scrubbers in sync by hand
 * if the patterns change on either side.
 *
 * Posture: FULLY INERT unless BOTH `sentryEnabled` is true AND a non-empty
 * `sentryDsn` is supplied.
 *  - `initSentryErrorReporting()` never calls `Sentry.init()` otherwise — no
 *    network calls, no global handler registration.
 *  - `reportExceptionToSentry()` is a pure no-op in the same case, so
 *    `GlobalErrorHandler.reportToSink()` doesn't need its own gate check.
 *
 * The SDK is `@sentry/browser`, NOT `@sentry/angular`: nothing
 * Angular-specific (`TraceService`, `createErrorHandler`, routing
 * instrumentation) is used, and `@sentry/angular@8`'s peer range caps at
 * Angular 19 while this repo is past it. It is loaded via a lazy dynamic
 * `import()` (top-level import is type-only, erased at compile time), so the
 * SDK contributes ZERO bytes to any app's initial bundle while the flags are
 * off — the chunk is only fetched on the first enabled init/report call.
 * Consequence of lazy init: an error thrown in the narrow window before the
 * SDK chunk finishes loading is dropped by Sentry (capture before init) —
 * acceptable for best-effort, sampled observability.
 */

/** Everything the runtime uses off the SDK — resolved lazily, see module doc. */
type SentryBrowserSdk = typeof import('@sentry/browser');

let sdkLoad: Promise<SentryBrowserSdk> | null = null;

/** Lazily load (and cache) the SDK chunk. Only ever called behind the enabled gate. */
function loadSentrySdk(): Promise<SentryBrowserSdk> {
  return (sdkLoad ??= import('@sentry/browser'));
}

/** The subset of `Env` (`libs/app-config`) this module reads. */
export interface SentryFrontendConfigI {
  readonly sentryEnabled?: boolean;
  readonly sentryDsn?: string;
}

/** Single source of truth for "is frontend Sentry on" — mirrors `isSentryEnabled()` backend-side. */
export function isSentryReportingEnabled(config: SentryFrontendConfigI): boolean {
  return Boolean(config.sentryEnabled) && typeof config.sentryDsn === 'string' && config.sentryDsn.trim().length > 0;
}

// ---- PII / secret scrubbing (mirrors apps/back/api/src/app/sentry.config.ts) ----

const REDACTED = '[REDACTED]';

/** Email addresses. Same pattern as `redact-pii.ts` backend-side. */
const EMAIL_RE = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi;

/** Phone numbers (North-American-ish but permissive). Same pattern as `redact-pii.ts` backend-side. */
const PHONE_RE = /(?<![\dA-Fa-f-])(?:\+?\d{1,3}[\s.-]?)?(?:\(\d{3}\)|\d{3})[\s.-]?\d{3}[\s.-]?\d{4}(?!\d)/g;

/** Object keys whose value should be masked wholesale (bare names, etc). */
const PII_KEY_RE =
  /(^|[._-])(email|phone|mobile|firstname|lastname|fullname|name|customername|legacyemail|emailaddress|phonenumber)([._-]|$)/i;

/** Keys that look name-ish but are structural, not personal — never masked. */
const SAFE_KEY_RE = /(filename|hostname|username|classname|eventname|tablename|fieldname|pathname)/i;

/** Database connection URIs — scheme kept, credentials/host masked. */
const DB_URI_RE = /postgres(?:ql)?:\/\/[^\s'"]+/gi;

/** JWT-looking tokens. */
const JWT_RE = /\beyJ[A-Za-z0-9_-]{8,}(?:\.[A-Za-z0-9_-]+){1,2}/g;

/** `Bearer <token>` fragments. */
const BEARER_RE = /\bBearer\s+[A-Za-z0-9._~+/=-]{8,}/g;

/** Mask DB URIs / JWTs / Bearer tokens inside a free-form string. Non-string input passes through. */
function redactSecrets<T>(value: T): T {
  if (typeof value !== 'string' || value.length === 0) {
    return value;
  }
  const masked = value
    .replace(DB_URI_RE, match => `${match.slice(0, match.indexOf('://'))}://${REDACTED}`)
    .replace(BEARER_RE, `Bearer ${REDACTED}`)
    .replace(JWT_RE, REDACTED);
  return masked as unknown as T;
}

/** Mask email/phone substrings inside a free-form string. Non-string input passes through. */
function redactPii<T>(value: T): T {
  if (typeof value !== 'string' || value.length === 0) {
    return value;
  }
  return value.replace(EMAIL_RE, REDACTED).replace(PHONE_RE, REDACTED) as unknown as T;
}

/** Combined text scrubber: PII (email/phone) + secret shapes. */
function scrubText<T>(value: T): T {
  return redactPii(redactSecrets(value));
}

function isPiiKey(key: string): boolean {
  return PII_KEY_RE.test(key) && !SAFE_KEY_RE.test(key);
}

/** Deep-walk a structured value, masking PII-bearing keys and scrubbing every string. */
function scrubMeta<T>(input: T, seen: WeakSet<object> = new WeakSet()): T {
  if (input === null || input === undefined) {
    return input;
  }
  if (typeof input === 'string') {
    return scrubText(input);
  }
  if (typeof input !== 'object') {
    return input;
  }
  if (seen.has(input as object)) {
    return input;
  }
  seen.add(input as object);
  if (Array.isArray(input)) {
    return input.map(item => scrubMeta(item, seen)) as unknown as T;
  }
  const out: Record<string, unknown> = {};
  for (const [key, val] of Object.entries(input as Record<string, unknown>)) {
    out[key] = typeof val === 'string' && isPiiKey(key) ? REDACTED : scrubMeta(val, seen);
  }
  return out as unknown as T;
}

const SENSITIVE_HEADER_NAMES = new Set(['authorization', 'cookie', 'proxy-authorization', 'x-api-key']);

/** Drop auth-bearing headers; keep the rest (scrubbed for stray PII/secret text). */
function scrubHeaders(headers: Record<string, string>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [key, value] of Object.entries(headers)) {
    if (SENSITIVE_HEADER_NAMES.has(key.toLowerCase())) {
      continue;
    }
    out[key] = scrubText(value);
  }
  return out;
}

/**
 * Sentry `beforeSend` hook. Drops the request query string/cookies and the
 * `user` object outright, strips auth-bearing headers, and scrubs
 * email/phone + secret-shape text (JWTs, Bearer tokens, DB URIs) from the
 * message, exception values, `extra`, and breadcrumbs. Pure function — never
 * mutates the input event. Never returns `null`: events are scrubbed, not
 * dropped, so a captured error can't be silently starved by a scrubbing bug.
 *
 * KNOWN GAP: `event.contexts`, `event.tags`, and `event.transaction` are NOT
 * scrubbed. Nothing populates them today (the only capture path is
 * `captureException(error)` — no `setContext`/`setTag` anywhere), but if a
 * future call site adds them, extend the scrubbing here first.
 */
export function scrubSentryBrowserEvent(event: SentryErrorEvent): SentryErrorEvent {
  const scrubbed: SentryErrorEvent = { ...event };

  if (scrubbed.request) {
    const { url, method, headers } = scrubbed.request;
    scrubbed.request = {
      ...(url && { url: scrubText(url) }),
      ...(method && { method }),
      ...(headers && { headers: scrubHeaders(headers as Record<string, string>) }),
    };
  }

  if (scrubbed.user) {
    scrubbed.user = undefined;
  }

  if (typeof scrubbed.message === 'string') {
    scrubbed.message = scrubText(scrubbed.message);
  }

  if (scrubbed.exception?.values) {
    scrubbed.exception = {
      ...scrubbed.exception,
      values: scrubbed.exception.values.map(value => ({
        ...value,
        ...(typeof value.value === 'string' && { value: scrubText(value.value) }),
      })),
    };
  }

  if (scrubbed.extra) {
    scrubbed.extra = scrubMeta(scrubbed.extra);
  }

  if (scrubbed.breadcrumbs) {
    scrubbed.breadcrumbs = scrubbed.breadcrumbs.map(breadcrumb => ({
      ...breadcrumb,
      ...(typeof breadcrumb.message === 'string' && { message: scrubText(breadcrumb.message) }),
      ...(breadcrumb.data && { data: scrubMeta(breadcrumb.data) }),
    }));
  }

  return scrubbed;
}

/**
 * Error events sent to Sentry, as a fraction of all captured exceptions.
 * Deliberately < 1.0 (in addition to tracing/replay being fully off) to keep
 * a free/low-volume Sentry plan from being exhausted by a noisy client-side
 * error loop; raise this once real quota/usage data justifies it.
 */
const CONSERVATIVE_ERROR_SAMPLE_RATE = 0.25;

/**
 * Build the `Sentry.init()` options. Separated from `initSentryErrorReporting()`
 * so the shape (no tracing/replay/session-capture, the scrubber) has its own
 * unit coverage without needing to spy on the SDK's `init` call.
 */
export function buildSentryBrowserOptions(dsn: string): BrowserOptions {
  return {
    dsn,
    // An empty array REPLACES the SDK's default integration set entirely — this
    // drops not just tracing/replay but also Dedupe, LinkedErrors, Breadcrumbs,
    // InboundFilters, HttpContext, etc. Deliberate: raw error capture only, and
    // the less auto-collected context there is, the less there is to scrub.
    integrations: [],
    tracesSampleRate: 0,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
    sampleRate: CONSERVATIVE_ERROR_SAMPLE_RATE,
    // We scrub explicitly via beforeSend; never let the SDK auto-attach IP/cookies/headers.
    sendDefaultPii: false,
    beforeSend: event => scrubSentryBrowserEvent(event),
  };
}

let initStarted = false;
/** Resolves to the initialized SDK, or `null` if the chunk failed to load. */
let initSettled: Promise<SentryBrowserSdk | null> | null = null;

/**
 * Initialize the frontend Sentry SDK once at app bootstrap. A no-op unless
 * BOTH `sentryEnabled` and a non-empty `sentryDsn` are set (inert-by-default,
 * matching D6) — call once per app via `provideSentryErrorReporting()`.
 * Kicks off the lazy SDK-chunk load and calls `init()` when it lands;
 * synchronous by signature, fire-and-forget by design (a chunk-load failure
 * degrades to no reporting, never to a boot failure).
 */
export function initSentryErrorReporting(config: SentryFrontendConfigI): void {
  if (initStarted || !isSentryReportingEnabled(config)) {
    return;
  }
  initStarted = true;
  initSettled = loadSentrySdk()
    .then(sdk => {
      sdk.init(buildSentryBrowserOptions(config.sentryDsn as string));
      return sdk;
    })
    .catch(() => null);
}

/**
 * Report an uncaught error to Sentry — the single call site
 * `GlobalErrorHandler.reportToSink()` uses. A no-op when Sentry reporting
 * isn't enabled, so the call site doesn't need its own gate check. MUST NOT
 * throw (best-effort observability, not UX-critical — same contract as the
 * backend's `captureApiException`). Waits on the init chain when it exists so
 * capture never races ahead of `init()`; if `init` was never invoked
 * (misconfigured bootstrap), the SDK itself drops the event — still silent.
 */
export function reportExceptionToSentry(error: unknown, config: SentryFrontendConfigI): void {
  if (!isSentryReportingEnabled(config)) {
    return;
  }
  void (initSettled ?? loadSentrySdk().catch(() => null))
    .then(sdk => sdk?.captureException(error))
    .catch(() => {
      // Swallow: Sentry is best-effort observability, not UX-critical.
    });
}

/** Test-only: reset the one-time init guard between spec files. */
export function resetSentryInitGuardForTests(): void {
  initStarted = false;
  initSettled = null;
}

/**
 * App-bootstrap provider — add to each of the 4 apps' `appConfig.providers`
 * (alongside the existing `{ provide: ErrorHandler, useClass: GlobalErrorHandler }`
 * entry) to initialize Sentry once at startup:
 *
 * ```ts
 * provideSentryErrorReporting(environment),
 * ```
 *
 * A no-op provider when Sentry reporting is disabled — see
 * `initSentryErrorReporting()`.
 */
export function provideSentryErrorReporting(config: SentryFrontendConfigI) {
  return provideAppInitializer(() => initSentryErrorReporting(config));
}
